import { v4 as uuidv4 } from 'uuid';
import { ChatMessageModel, PlainTextMessageModel } from '@klant/conversational-connector/models/ChatMessage.model';
import { TypeModel } from '@klant/conversational-connector/models/Type.model';
import { ScreenreaderService } from '@klant/conversational-connector/services/screenreader/screenreader.service';
import { UnreadMessageService } from '@klant/conversational-connector/services/unread-message-service/unread-message.service';

import { inject, Injectable } from '@angular/core';

import { ActionModel, MessageModel, MessageType, PendingMessage } from '../../klant-conversational-connector.model';
import { WebsocketService } from '../wsservice/websocket.service';
import { ConversationalChatStore } from '../../../../signal-stores/conversational-chat-store/conversational-chat.store';

@Injectable({ providedIn: 'root' })
export class MessageHandlerService {
    private readonly chatStore = inject(ConversationalChatStore);
    private readonly webSocketService = inject(WebsocketService);
    private readonly screenreaderService = inject(ScreenreaderService);
    private readonly unreadMessageService = inject(UnreadMessageService);

    public sendMessage(text: string): void {
        const message: MessageModel = {
            messageType: MessageType.MESSAGE,
            transactionId: uuidv4(),
            text
        };

        this.chatStore.addPendingMessage(message);
        this.addClientMessage(message.text, message.transactionId);
        this.webSocketService.sendMessage(message);
    }

    public sendAction(actionModel: ActionModel): void {
        const action: ActionModel = {
            ...actionModel,
            transactionId: uuidv4()
        };

        this.chatStore.addPendingMessage(action);
        this.addClientMessage(action.text, action.transactionId);
        this.webSocketService.sendPickChoiceAction(action);
    }

    public handleIncomingMessage(chatMessage: ChatMessageModel): void {
        const isMessageFromBotOrAgent = !chatMessage.fromClient;

        if (isMessageFromBotOrAgent) {
            this.chatStore.addChatMessage(chatMessage);
            this.screenreaderService.screenReaderMessages(chatMessage);
            this.unreadMessageService.markMessagesAsRead([chatMessage]);
        } else {
            this.chatStore.removePendingMessage(chatMessage.transactionId);
            this.chatStore.updateChatMessage(chatMessage);
        }

        this.scrollToBottomDelay();
    }

    public handleParticipantMessage(message: ChatMessageModel): void {
        this.chatStore.addChatMessage(message);
        this.scrollToBottomDelay();
    }

    public resendPendingMessages(): void {
        this.chatStore.pendingMessages().forEach((pendingMessage: PendingMessage) => {
            this.addClientMessage(pendingMessage.text, pendingMessage.transactionId);
            this.resendPendingMessage(pendingMessage);
        });
    }

    private addClientMessage(text: string, transactionId: string): void {
        const clientChatMessage: PlainTextMessageModel = {
            type: TypeModel.PLAIN_TEXT_MESSAGE,
            text,
            fromClient: true,
            transactionId,
            occurredAt: Date.now() * 1000
        };
        this.chatStore.addChatMessage(clientChatMessage);
    }

    private resendPendingMessage(pendingMessage: PendingMessage): void {
        if (pendingMessage.messageType === MessageType.MESSAGE) {
            this.webSocketService.sendMessage(pendingMessage as MessageModel);
        } else {
            this.webSocketService.sendPickChoiceAction(pendingMessage as ActionModel);
        }
    }

    private scrollToBottomDelay(): void {
        window.setTimeout(() => {}, 100);
    }
}
